import React from 'react';
import { Button } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { TitleFormattedMessage } from '../../components/titleFormattedMessage';
import PropdocsModal from '../../components/modal/PropdocsModal';

type RestrictedEditingToggleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Current state — true means the template is currently restricted. */
  isRestricted: boolean;
  templateName: string;
};

/**
 * Confirm turning restricted editing on/off for a docx-native template.
 *
 * Turning OFF is non-destructive: it STOPS ENFORCING the restriction but leaves
 * the marked editable regions in the document (dormant), so it's fully
 * reversible — a single clear confirm, not a destructive double-confirm.
 */
const RestrictedEditingToggleModal: React.FC<RestrictedEditingToggleModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isRestricted,
  templateName,
}) => {
  const title = isRestricted ? 'Make Fully Editable' : 'Make Restricted';
  const message = isRestricted
    ? `"${templateName}" will become fully editable — the restriction stops being enforced. The areas you marked editable stay saved in the document (dormant), so you can switch back to restricted at any time without re-marking them.`
    : `"${templateName}" will become a restricted template: only the areas you mark as editable can be changed in documents created from it. You can mark editable areas in the template editor.`;
  const buttonText = isRestricted ? 'Make Fully Editable' : 'Make Restricted';

  return (
    <PropdocsModal
      show={isOpen}
      setShow={onClose}
      size="sm"
      title={
        <FormattedMessage
          id={`title.${isRestricted ? 'make-fully-editable' : 'make-restricted'}`}
          defaultMessage={title}
        />
      }
    >
      <>
        <div style={{ marginBottom: '1.5rem' }}>
          <p>{message}</p>
          <p style={{ marginTop: '1rem', fontWeight: 'bold' }}>
            <FormattedMessage id="confirm.continue-question" defaultMessage="Do you want to continue?" />
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
          <Button variant="link" className="text-secondary" onClick={onClose}>
            <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
          </Button>
          <Button variant="primary" onClick={onConfirm}>
            <TitleFormattedMessage
              id={`cta.${isRestricted ? 'make-fully-editable' : 'make-restricted'}`}
              defaultMessage={buttonText}
            />
          </Button>
        </div>
      </>
    </PropdocsModal>
  );
};

export default RestrictedEditingToggleModal;
